/**
 * Edit preset modal - full edit, bulk, reorder
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { Button } from '@/components/ui';
import { backdropClick } from '@/components/ui';
import { Input } from '@/components/ui';
import { Pencil, X } from 'lucide-react';

import type { Preset, CreatePresetPayload } from '../shared';
import type { PresetsModalCatalog } from '../shared';
import { GroupSelect } from '../shared';

import { EditPresetBulkBar } from './EditPresetBulkBar';
import { EditPresetItemRow } from './EditPresetItemRow';
import { useEditPresetForm } from './useEditPresetForm';
import { PresetFoldersPicker } from '../folders';

export interface EditPresetModalProps {
  countryKey: string;
  preset: Preset | null;
  catalog: PresetsModalCatalog;
  availableGroupNames?: string[];
  isBusy: boolean;
  onClose: () => void;
  onSave: (payload: {
    id: string;
    name: string;
    groupName?: string;
    folderIds?: string[];
    items: CreatePresetPayload['items'];
  }) => void;
}

export const EditPresetModal: FC<EditPresetModalProps> = ({
  countryKey,
  preset,
  catalog,
  availableGroupNames = [],
  isBusy,
  onClose,
  onSave,
}) => {
  const form = useEditPresetForm(preset, catalog);
  const [presetFolderIds, setPresetFolderIds] = useState<string[]>(
    () => preset?.folders?.map((t) => t.id) ?? [],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = form.items.map((i) => i.id);
      const oldIdx = ids.indexOf(active.id as string);
      const newIdx = ids.indexOf(over.id as string);
      if (oldIdx === -1 || newIdx === -1) return;
      form.reorder(oldIdx, newIdx);
    },
    [form.items, form.reorder],
  );

  const handleSave = useCallback(() => {
    if (!preset) return;
    onSave({ id: preset.id, ...form.buildPayload(), folderIds: presetFolderIds });
  }, [preset, form, onSave, presetFolderIds]);

  if (!preset) return null;

  const selectedCount = form.selectedIds.size;
  const canSave = form.name.trim().length > 0 && form.items.length > 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      onClick={backdropClick(onClose)}
    >
      <div
        className="flex max-h-[90vh] w-full min-w-[720px] max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Pencil className="h-4 w-4 shrink-0" />
            Edit preset
          </h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          <div className="flex flex-nowrap items-end gap-4">
            <div className="min-w-0 flex-1">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Preset name</label>
              <Input
                value={form.name}
                onChange={(e) => form.setName(e.target.value)}
                placeholder="Preset name"
                maxLength={120}
                className="h-9"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Group (optional)</label>
              <GroupSelect
                value={form.groupName}
                onChange={form.setGroupName}
                availableGroupNames={availableGroupNames}
                placeholder="Group"
                className="h-9"
              />
            </div>
          </div>
          <PresetFoldersPicker
            countryKey={countryKey}
            value={presetFolderIds}
            onChange={setPresetFolderIds}
            disabled={isBusy}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Items ({form.items.length})</span>
            <Button variant="outline" size="sm" onClick={form.toggleSelectAll}>
              {selectedCount === form.items.length && form.items.length > 0 ? 'Deselect all' : 'Select all'}
            </Button>
          </div>
          {selectedCount > 0 && (
            <EditPresetBulkBar
              count={selectedCount}
              catalog={catalog}
              onApply={form.applyBulk}
              onClear={form.clearSelection}
              onRemove={form.removeSelectedItems}
            />
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={form.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div className="overflow-x-auto rounded-lg border border-border/60">
                <div className="grid w-full grid-cols-[2.5rem_2.5rem_2.5rem_3rem_1fr_1fr_1fr_3.5rem_1fr_minmax(0,1fr)] items-center gap-3 border-b border-border/60 bg-muted/20 px-3 py-2 text-xs font-medium text-muted-foreground">
                  <span className="flex justify-center" />
                  <span className="flex justify-center" />
                  <span className="flex justify-center" />
                  <span className="min-w-0 truncate pr-2 text-sm">#</span>
                  <span className="min-w-0 truncate pr-2 text-sm">Campaign</span>
                  <span className="min-w-0 truncate pr-2 text-sm">Gateway</span>
                  <span className="min-w-0 truncate pr-2 text-sm">Leads</span>
                  <span className="min-w-0 pr-2 text-right text-sm tabular-nums">Amt</span>
                  <span className="min-w-0 truncate pr-2 text-sm">TLD</span>
                  <span className="min-w-0 truncate pr-2 text-sm">Static tag</span>
                </div>
                <div className="flex flex-col">
                  {form.items.map((item, idx) => (
                    <EditPresetItemRow
                      key={item.id}
                      item={item}
                      index={idx}
                      catalog={catalog}
                      isSelected={form.selectedIds.has(item.id)}
                      onToggleSelect={() => form.toggleSelect(item.id)}
                      onUpdate={(patch) => form.updateItem(item.id, patch)}
                    />
                  ))}
                </div>
              </div>
            </SortableContext>
          </DndContext>
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-border p-4">
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave || isBusy}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
